<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Corporate Quote List</h1>
        <span class="page-subtitle">Incoming quote requests from corporate clients</span>
      </div>
    </div>

    <!-- ── Search ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">
        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search quotes..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>
        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Services</th>
            <th>Budget</th>
            <th>Est. Qty</th>
            <th>Date Required</th>
            <th>Price / Gift</th>
            <th>Comments</th>
            <th>Date</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="response in responses"
              :key="response.id"
              :class="['dd-table__row', !response.is_seen ? 'dd-table__row--new' : '']"
          >
            <td>
              <div class="customer-info">
                <div class="customer-avatar" :class="!response.is_seen ? 'customer-avatar--new' : ''">
                  {{ (response.fields.first_name || '?')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="customer-name">
                    {{ `${response.fields.first_name} ${response.fields.last_name}`.trim() || '—' }}
                  </div>
                  <span class="new-badge" v-if="!response.is_seen">New</span>
                </div>
              </div>
            </td>
            <td><span class="company-name">{{ response.fields.company_name || '—' }}</span></td>
            <td class="email-cell">{{ response.fields.email || '—' }}</td>
            <td class="text-muted">{{ response.fields.phone_number || '—' }}</td>
            <td>
              <div class="services-wrap" v-if="response.fields.services && getServicesArray(response.fields.services).length">
                  <span
                      v-for="(svc, i) in getServicesArray(response.fields.services).slice(0, 2)"
                      :key="i"
                      class="service-badge"
                  >{{ svc }}</span>
                <span
                    v-if="getServicesArray(response.fields.services).length > 2"
                    class="service-more"
                    :id="`svc-${response.id}`"
                >+{{ getServicesArray(response.fields.services).length - 2 }}</span>
                <b-tooltip
                    v-if="getServicesArray(response.fields.services).length > 2"
                    :target="`svc-${response.id}`"
                    triggers="hover"
                    placement="top"
                >
                  <div class="tooltip-services">
                    <div
                        v-for="(svc, i) in getServicesArray(response.fields.services).slice(2)"
                        :key="i"
                        class="tooltip-service-item"
                    >{{ svc }}</div>
                  </div>
                </b-tooltip>
              </div>
              <span class="text-muted" v-else>—</span>
            </td>
            <td class="text-muted">{{ response.fields.budget || '—' }}</td>
            <td class="text-muted">{{ response.fields.estimated_qty || '—' }}</td>
            <td class="date-cell">{{ response.fields.date_required_by || '—' }}</td>
            <td class="text-muted">{{ response.fields.price_per_gift || '—' }}</td>
            <td>
                <span
                    class="comment-text"
                    v-if="response.fields.comments"
                    :title="response.fields.comments"
                >{{ short(response.fields.comments, 30) }}</span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td class="date-cell">{{ formatDate(response.created_at) }}</td>
            <td>
              <button class="btn-create" @click.prevent="showCreateModal(response.fields)">
                <i class="fa fa-building"></i> Create Corporate
              </button>
            </td>
          </tr>

          <tr v-if="responses && responses.length === 0">
            <td colspan="12" class="empty-state">
              <i class="fa fa-inbox"></i>
              <div>No quote requests found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">Page {{ page }} of {{ lastPage }}</div>
        <div class="pagination-btns">
          <button class="page-btn" :disabled="page === 1" @click="previousPage()">
            <i class="fa fa-chevron-left"></i> Prev
          </button>
          <span class="page-current">{{ page }}</span>
          <button class="page-btn" :disabled="page === lastPage" @click="nextPage()">
            Next <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Create Corporate Modal ── -->
    <modal name="create-modal" height="auto" :scrollable="true">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Create Corporate</span>
          <button class="dd-modal__close" @click="$modal.hide('create-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">

          <div class="modal-section-title">Contact Info</div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">First Name <span class="required">*</span></label>
              <input v-model="createForm.user.first_name" class="dd-input" placeholder="First name" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Last Name</label>
              <input v-model="createForm.user.last_name" class="dd-input" placeholder="Last name" />
            </div>
            <div class="form-field">
              <label class="filter-label">Email <span class="required">*</span></label>
              <input v-model="createForm.user.email" type="email" class="dd-input" placeholder="email@company.com" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Phone <span class="required">*</span></label>
              <input v-model="createForm.user.phone_number" class="dd-input" placeholder="+971..." required />
            </div>
          </div>

          <div class="section-divider"><span>Company Info</span></div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Company Name <span class="required">*</span></label>
              <input v-model="createForm.name" class="dd-input" placeholder="Company name" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Website URL</label>
              <input v-model="createForm.website" class="dd-input" placeholder="https://..." />
            </div>
          </div>

          <div class="section-divider"><span>Services Interested</span></div>
          <div class="services-checkboxes">
            <label v-for="svc in services" :key="svc" class="checkbox-label">
              <input type="checkbox" class="checkbox-input" :value="svc" v-model="createForm.services" />
              <span>{{ svc }}</span>
            </label>
          </div>

          <div class="section-divider"><span>Address</span></div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Country</label>
              <country-search-box @selected="(id) => createForm.user.address.country_id = id"></country-search-box>
            </div>
            <div class="form-field">
              <label class="filter-label">City</label>
              <city-search-box @selected="(id) => createForm.user.address.city_id = id"></city-search-box>
            </div>
            <div class="form-field form-field--full">
              <label class="filter-label">Address</label>
              <input v-model="createForm.user.address.address" class="dd-input" placeholder="Street address" />
            </div>
            <div class="form-field">
              <label class="filter-label">Postcode</label>
              <input v-model="createForm.user.address.post_code" class="dd-input" placeholder="Postcode" />
            </div>
          </div>

          <ul class="error-list" v-if="createForm.errors && createForm.errors.length">
            <li v-for="error in createForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('create-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="createCorporate">
            <i class="fa fa-building"></i> Create
          </button>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config, short } from "../helpers";
import CountrySearchBox from "../components/CountrySearchBox.vue";
import CitySearchBox from "../components/CitySearchBox.vue";

export default {
  name: "CorporateQuoteList",
  components: {
    "country-search-box": CountrySearchBox,
    "city-search-box": CitySearchBox,
  },
  data() {
    return {
      url: "",
      responses: [],
      inputFilters: { q: null },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      services: [
        "Rewards & Recognition", "Long Service Awards", "Motivation",
        "On Pack Promotions", "Corporate Gifts", "Team Building",
        "Sales Incentives", "Themed Incentives", "Corporate Hospitality",
        "Marketing Promotions", "Referral Schemes", "Employee Benefits", "Prizes",
      ],
      createForm: {
        user: { first_name: "", address: { address: "" } },
        services: [],
      },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/forms/corporate-quote-list/responses";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page, see: 1 };
      if (this.isFiltered) myData = { ...myData, ...this.filters };
      axios.get(this.url, { params: myData }).then((r) => {
        this.responses = r.data.data;
        this.page      = r.data.meta.current_page;
        this.lastPage  = r.data.meta.last_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      const filters = {};
      if (this.inputFilters.q) filters.q = this.inputFilters.q;
      this.filters = filters;
      this.loadData();
    },

    createCorporate(event) {
      event.preventDefault();
      axios
          .post(config("BACKEND_ROOT") + "/admin/corporates", this.createForm)
          .then(() => {
            this.createForm = { user: { first_name: "", address: { address: "" } }, services: [] };
            alert("Corporate created successfully!");
            this.$modal.hide("create-modal");
            this.loadData();
          })
          .catch((e) => {
            this.createForm.errors = Object.values(e.response.data.errors).flat();
          });
    },

    showCreateModal(fields) {
      this.createForm = {
        user: {
          first_name:   fields.first_name,
          last_name:    fields.last_name,
          email:        fields.email,
          phone_number: fields.phone_number,
          address:      { address: "" },
        },
        name:     fields.company_name,
        website:  fields.website,
        services: this.getServicesArray(fields.services),
        budget:   fields.budget,
        errors:   [],
      };
      this.$modal.show("create-modal");
    },

    nextPage()     { if (this.page < this.lastPage) { this.page++; this.loadData(); } },
    previousPage() { if (this.page > 1) { this.page--; this.loadData(); } },

    formatDate(date) {
      if (!date) return '—';
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },

    getServicesForHuman(services) {
      return this.getServicesArray(services).join(', ');
    },

    getServicesArray(services) {
      if (!services) return [];
      if (typeof services === 'string') return [services];
      if (Array.isArray(services)) return services;
      return [];
    },

    short(string, limit) {
      return short(string, limit);
    },
  },
};
</script>

<style scoped>
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }
.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

/* Filter */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }
.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* Table */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row { transition: background .12s; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table__row--new td { background: rgba(34,197,94,.05); border-left: 3px solid #22c55e; }
.dd-table__row--new:first-child td:first-child { border-left: 3px solid #22c55e; }
.dd-table tr:last-child td { border-bottom: none; }

/* Cells */
.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 30px; height: 30px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.customer-avatar--new { background: rgba(34,197,94,.15); color: #22c55e; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }
.new-badge       { display: inline-flex; align-items: center; padding: 1px 6px; border-radius: 20px; font-size: 10px; font-weight: 600; background: rgba(34,197,94,.15); color: #22c55e; border: 1px solid rgba(34,197,94,.3); }
.company-name    { font-weight: 500; color: var(--text-1); }
.email-cell      { font-size: 12px; color: var(--text-2); }
.text-muted      { color: var(--text-3); font-size: 12px; }
.date-cell       { font-size: 12px; color: var(--text-3); white-space: nowrap; }
.comment-text    { font-size: 12px; color: var(--text-2); max-width: 150px; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: help; border-bottom: 1px dashed var(--border-2); }

/* Services */
.services-wrap { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.service-badge { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 20px; font-size: 10px; font-weight: 500; background: rgba(129,140,248,.1); color: #818cf8; border: 1px solid rgba(129,140,248,.2); white-space: nowrap; }
.service-more  { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 20px; font-size: 10px; font-weight: 500; background: var(--bg-hover); color: var(--text-3); border: 1px solid var(--border); cursor: default; }

/* Tooltip */
.tooltip-services { text-align: left; }
.tooltip-service-item { padding: 3px 0; font-size: 12px; color: #fff; white-space: nowrap; }
.tooltip-service-item + .tooltip-service-item { border-top: 1px solid rgba(255,255,255,.1); }

/* Create button */
.btn-create { height: 30px; padding: 0 12px; background: rgba(244,47,75,.1); border: 1px solid rgba(244,47,75,.3); border-radius: 7px; color: var(--accent); font-size: 12px; font-weight: 500; cursor: pointer; white-space: nowrap; display: flex; align-items: center; gap: 6px; transition: background .15s; }
.btn-create:hover { background: rgba(244,47,75,.18); }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.pagination-btns { display: flex; align-items: center; gap: 8px; }
.page-btn { height: 32px; padding: 0 12px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; color: var(--text-2); font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 5px; transition: border-color .15s, color .15s; }
.page-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-current { font-size: 13px; font-weight: 500; color: var(--text-1); padding: 0 4px; }

.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Modal */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

.modal-section-title { font-size: 11px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: .75rem; }
.section-divider { display: flex; align-items: center; gap: 12px; margin: 1.25rem 0 .75rem; color: var(--text-3); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }
.section-divider::before, .section-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }

.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: span 2; }
.required { color: var(--accent); }

.services-checkboxes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.checkbox-label { display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: 12px; color: var(--text-2); padding: 6px 8px; border-radius: 7px; border: 1px solid var(--border); background: var(--bg-hover); transition: border-color .15s; }
.checkbox-label:hover { border-color: var(--accent); color: var(--text-1); }
.checkbox-input { accent-color: var(--accent); width: 14px; height: 14px; cursor: pointer; flex-shrink: 0; }

.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }
.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }

@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-field--full { grid-column: span 1; }
  .services-checkboxes { grid-template-columns: 1fr 1fr; }
}
</style>